/**
 * Minimal runtime decoding at the network boundary.
 *
 * Responses arrive as `unknown`. Instead of asserting types (or sprinkling `any`),
 * each payload is narrowed explicitly. A mismatch raises `DecodeError`, which the
 * client converts into an `invalid_response` ApiError so the UI can say that the
 * backend contract did not match rather than rendering `undefined` as data.
 */

import type { FieldIssue } from './errors';

export class DecodeError extends Error {
  readonly path: string;

  constructor(path: string, expected: string) {
    super(`字段 ${path} 期望 ${expected}`);
    this.name = 'DecodeError';
    this.path = path;
  }
}

export type Decoder<T> = (value: unknown, path: string) => T;

/**
 * Rejects a payload the decoder cannot make sense of, naming what was expected.
 *
 * Kept as a standalone helper because every decoder in this module funnels through
 * it, so the error type and message format stay identical everywhere.
 */
function fail(path: string, expected: string): never {
  throw new DecodeError(path, expected);
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Marker decoder for endpoints that answer with a body the client does not read.
 *
 * Used where success is the whole answer — a `204` from `POST auth/logout`, for
 * instance. It deliberately performs no shape check, because asserting a shape for
 * a body nobody consumes would invent a contract requirement the API does not
 * have; the transport's status handling is what decides success.
 */
export const anyBody: Decoder<unknown> = (value) => value;

export const str: Decoder<string> = (value, path) =>
  typeof value === 'string' ? value : fail(path, '字符串');

/**
 * Allows an explicit `null` from the API; rejects `undefined` and wrong types.
 *
 * The distinction matters for content records: the server sends `null` for a field
 * a tombstone or a body-less source record does not have, and never an empty
 * string. Accepting `undefined` here would also let a field the response simply
 * omitted decode as a legitimate "no value", which is contract drift rather than
 * data — that has to fail at the boundary.
 */
export const nullableStr: Decoder<string | null> = (value, path) => {
  if (value === null) return null;
  if (typeof value === 'string') return value;
  return fail(path, '字符串或 null');
};

export const num: Decoder<number> = (value, path) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fail(path, '数字');

/**
 * A JSON integer. `source_version` is a monotonically increasing observation
 * counter on the server side, so a fractional or non-finite value is a contract
 * violation rather than something to round: silently truncating 3.7 to 3 would
 * report a revision that does not exist.
 */
export const int: Decoder<number> = (value, path) =>
  typeof value === 'number' && Number.isInteger(value) ? value : fail(path, '整数');

export const nullableInt: Decoder<number | null> = (value, path) =>
  value === null ? null : int(value, path);

export const bool: Decoder<boolean> = (value, path) =>
  typeof value === 'boolean' ? value : fail(path, '布尔值');

export function literal<T extends string>(...allowed: readonly T[]): Decoder<T> {
  return (value, path) => {
    if (typeof value === 'string' && (allowed as readonly string[]).includes(value)) {
      return value as T;
    }
    return fail(path, `取值之一 ${allowed.join(' / ')}`);
  };
}

export function optional<T>(decoder: Decoder<T>, fallback: T): Decoder<T> {
  return (value, path) => (value === undefined || value === null ? fallback : decoder(value, path));
}

export function arrayOf<T>(decoder: Decoder<T>): Decoder<T[]> {
  return (value, path) => {
    if (!Array.isArray(value)) return fail(path, '数组');
    return value.map((entry, index) => decoder(entry, `${path}[${index}]`));
  };
}

export function record<T>(decoder: Decoder<T>): Decoder<Record<string, T>> {
  return (value, path) => {
    if (!isRecord(value)) return fail(path, '对象');
    const result: Record<string, T> = {};
    for (const [key, entry] of Object.entries(value)) {
      result[key] = decoder(entry, `${path}.${key}`);
    }
    return result;
  };
}

export function object<T extends Record<string, unknown>>(shape: {
  readonly [K in keyof T]: Decoder<T[K]>;
}): Decoder<T> {
  return (value, path) => {
    if (!isRecord(value)) return fail(path, '对象');
    const result = {} as { [K in keyof T]: T[K] };
    for (const key of Object.keys(shape) as (keyof T & string)[]) {
      const decoder = shape[key];
      result[key] = decoder(value[key], `${path}.${key}`);
    }
    return result;
  };
}

/**
 * Reads the documented `{ items: [...] }` list envelope. Also tolerates a bare
 * array, because returning a top-level array is a common backend variation and
 * rejecting it would produce a misleading "contract mismatch" message.
 */
export function itemsEnvelope<T>(decoder: Decoder<T>): Decoder<{ items: T[] }> {
  return (value, path) => {
    if (Array.isArray(value)) {
      return { items: value.map((entry, index) => decoder(entry, `${path}[${index}]`)) };
    }
    if (isRecord(value) && Array.isArray(value['items'])) {
      const raw = value['items'];
      return { items: raw.map((entry, index) => decoder(entry, `${path}.items[${index}]`)) };
    }
    return fail(`${path}.items`, '数组或 { items: [] } 结构');
  };
}

/** Reads Problem Details style body content without trusting its shape. */
export function readProblemDetails(value: unknown): {
  code?: string;
  detail?: string;
  requestId?: string;
  fieldIssues: FieldIssue[];
} {
  if (!isRecord(value)) return { fieldIssues: [] };

  const code = typeof value['code'] === 'string' ? value['code'] : undefined;
  const detail =
    typeof value['detail'] === 'string'
      ? value['detail']
      : typeof value['message'] === 'string'
        ? value['message']
        : undefined;
  const requestId =
    typeof value['request_id'] === 'string'
      ? value['request_id']
      : typeof value['requestId'] === 'string'
        ? value['requestId']
        : undefined;

  const fieldIssues: FieldIssue[] = [];
  const rawErrors = value['errors'] ?? value['field_errors'];
  if (Array.isArray(rawErrors)) {
    for (const entry of rawErrors) {
      if (!isRecord(entry)) continue;
      const field = entry['field'] ?? entry['loc'] ?? entry['name'];
      const message = entry['message'] ?? entry['msg'] ?? entry['detail'];
      if (typeof field === 'string' && typeof message === 'string') {
        fieldIssues.push({ field, message });
      }
    }
  } else if (isRecord(rawErrors)) {
    for (const [field, message] of Object.entries(rawErrors)) {
      if (typeof message === 'string') fieldIssues.push({ field, message });
    }
  }

  const result: { code?: string; detail?: string; requestId?: string; fieldIssues: FieldIssue[] } =
    {
      fieldIssues,
    };
  if (code !== undefined) result.code = code;
  if (detail !== undefined) result.detail = detail;
  if (requestId !== undefined) result.requestId = requestId;
  return result;
}
